import { useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { USER_ROLES } from '@/lib/constants/roles';
import { usePermission } from '@/lib/permissions/use-permission';
import {
  useUpdateVoiceWebhook,
  useVoiceWebhookBodyParameters,
  useVoiceWebhookDetail,
} from '../api';
import { parseHeaderJson, rehydrateBodyTable } from '../payload';
import { VoiceWebhookForm, type VoiceWebhookFormValues } from './VoiceWebhookForm';

export function UpdateVoiceWebhookPage() {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const canEditAcl = usePermission('Manage Campaign.Webhook List', BASE_PERMISSION.EDIT);
  const canEdit = role !== USER_ROLES.S_ADMIN && canEditAcl;
  const { id: idParam } = useParams<{ id: string }>();
  const webhookId = Number(idParam);

  const detail = useVoiceWebhookDetail(webhookId);
  const bodyParams = useVoiceWebhookBodyParameters();
  const update = useUpdateVoiceWebhook();

  const initial = useMemo<VoiceWebhookFormValues | null>(() => {
    if (!detail.data || !bodyParams.data) return null;
    return {
      webhookName: detail.data.webhookName ?? '',
      url: detail.data.url ?? '',
      event: detail.data.event ?? '',
      markAsDefault: (detail.data.markAsDefault ?? 0) as 0 | 1,
      authToken: detail.data.authToken ?? '',
      headers: parseHeaderJson(detail.data.headerJson),
      bodyTable: rehydrateBodyTable(detail.data.bodyJson, bodyParams.data),
    };
  }, [detail.data, bodyParams.data]);

  // Super-admin still can't edit webhooks (unchanged); sub-super-admin now
  // needs the explicit ACL "edit" permission on Webhook List instead of a
  // blanket block.
  if (isSubOrSuper && !canEdit) return <Navigate to="/webhook-list" replace />;

  if (!initial) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading webhook…
      </div>
    );
  }

  return (
    <VoiceWebhookForm
      mode="update"
      initialValues={initial}
      isPending={update.isPending}
      onSubmit={(payload) => {
        update.mutate(
          {
            ...payload,
            id: webhookId,
            userId: Number(userId),
          },
          {
            onSuccess: (res) => {
              if (res?.message === 'Parameters Incorrect') {
                toast.error(res.message);
                return;
              }
              toast.success(res?.message ?? 'Webhook updated');
              navigate('/webhook-list');
            },
            onError: () => toast.error('Could not update the webhook.'),
          },
        );
      }}
    />
  );
}
